/**
 * Notify admin API client - email providers + SMS settings + test sends.
 */

import { apiFetch } from './api';

export type EmailProviderKind =
  | 'smtp'
  | 'resend'
  | 'ms_smtp'
  | 'ms_graph'
  | 'google_smtp';

export interface EmailProvider {
  id: string;
  tenantId: string;
  providerKind: EmailProviderKind;
  displayName: string;
  enabled: boolean;
  priority: number;
  fromAddress: string;
  fromName: string | null;
  replyTo: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUsername: string | null;
  smtpSecure: boolean;
  smtpStartTls: boolean;
  msalTenantId: string | null;
  msalClientId: string | null;
  msalAccountUpn: string | null;
  graphSenderUpn: string | null;
  googleClientId: string | null;
  googleSenderEmail: string | null;
  // Secret-set sentinels (true means stored; never returns plaintext).
  smtpPasswordSet: boolean;
  resendApiKeySet: boolean;
  msalClientSecretSet: boolean;
  googleClientSecretSet: boolean;
  googleRefreshTokenSet: boolean;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastFailureMessage: string | null;
  consecutiveFailures: number;
  createdAt: string;
  updatedAt: string;
}

export interface SmsSettings {
  id: string;
  tenantId: string;
  enabled: boolean;
  twilioAccountSid: string;
  twilioFromNumber: string;
  traiDltPeId: string | null;
  traiDltTemplateId: string | null;
  traiDltHeaderId: string | null;
  pagingRecipients: Array<{ name: string; phone: string; role?: string }>;
  authTokenSet: boolean;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastFailureMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listEmailProviders(): Promise<EmailProvider[]> {
  const r = await apiFetch<{ providers: EmailProvider[] }>('/v1/notify/email/providers');
  return r.providers;
}

export async function createEmailProvider(
  payload: Record<string, unknown>,
): Promise<EmailProvider> {
  return apiFetch<EmailProvider>('/v1/notify/email/providers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateEmailProvider(
  id: string,
  patch: Record<string, unknown>,
): Promise<EmailProvider> {
  return apiFetch<EmailProvider>(`/v1/notify/email/providers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteEmailProvider(id: string): Promise<void> {
  await apiFetch<void>(`/v1/notify/email/providers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function sendTestEmail(payload: {
  to: string;
  subject: string;
  body: string;
}): Promise<{ providerMessageId: string; providerKind: string; attemptNumber: number }> {
  return apiFetch('/v1/notify/email/test', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getSmsSettings(): Promise<SmsSettings | null> {
  try {
    return await apiFetch<SmsSettings>('/v1/notify/sms/settings');
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
}

export async function upsertSmsSettings(payload: {
  enabled?: boolean;
  twilioAccountSid: string;
  twilioAuthToken?: string;
  twilioFromNumber: string;
  traiDltPeId?: string;
  traiDltTemplateId?: string;
  traiDltHeaderId?: string;
  pagingRecipients?: Array<{ name: string; phone: string; role?: string }>;
}): Promise<SmsSettings> {
  return apiFetch<SmsSettings>('/v1/notify/sms/settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function sendTestSms(payload: { to: string; body: string }): Promise<{
  providerMessageSid: string;
  status: string;
}> {
  return apiFetch('/v1/notify/sms/test', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
